/**
 * The overdue-fee notice on Home (057 FR-001–FR-013, contracts/member-surface.md §2).
 *
 * It renders the SERVER's decision (`billingFacts.overdueNotice`) as given and
 * never re-derives it from the invoices. It is not a live region and moves no
 * focus: it is information on arrival, not an interruption. It carries no
 * amount, date or invoice number, and has no dismiss control. Its one action
 * opens Omat tiedot on the fee view through router state, so the fee view gets
 * no address of its own (D-PD-010).
 *
 * The attention styling uses the destructive token for the rail and the icon,
 * not the CMS banner colours (a content announcement is a different thing) and
 * not the `--color-warning-*` pair (green, despite the name). The text stays in
 * the foreground colour so it keeps AA contrast.
 */

import { type JSX, Suspense, useId } from 'react';
import { Link } from 'react-router';

import { useTranslate } from '../i18n/useTranslate.js';
import { FEE_VIEW_HANDOFF } from '../routes/profile/profileNavigationState.js';
import { useSession } from '../session/useSession.js';
import { AlertCircleIcon } from '../shell/icons.js';
import { PortErrorBoundary } from '../shell/PortErrorBoundary.js';
import { useMembershipBilling } from './useMembershipBilling.js';

const noticeClasses =
  'flex items-start gap-3 rounded-lg border border-destructive/40 border-l-4 border-l-destructive ' +
  'bg-destructive/5 p-4 text-foreground';
const iconClasses = 'mt-0.5 h-6 w-6 shrink-0 text-destructive';
const bodyClasses = 'flex min-w-0 flex-col gap-1';
const titleClasses = 'm-0 text-base font-bold';
const textClasses = 'm-0 text-sm';
const linkClasses = 'self-start text-sm font-medium text-primary underline hover:opacity-80';

export function OverdueFeeNotice(): JSX.Element | null {
  const t = useTranslate();
  const titleId = useId();
  const { billingFacts } = useMembershipBilling();

  if (billingFacts.overdueNotice !== 'show') return null;

  return (
    <section aria-labelledby={titleId} className={noticeClasses}>
      <AlertCircleIcon aria-hidden="true" className={iconClasses} />
      <div className={bodyClasses}>
        <h3 id={titleId} className={titleClasses}>
          {t('home.overdueFee.title')}
        </h3>
        <p className={textClasses}>{t('home.overdueFee.body')}</p>
        <Link to="/profile" state={FEE_VIEW_HANDOFF} className={linkClasses}>
          {t('home.overdueFee.action')}
        </Link>
      </div>
    </section>
  );
}

/**
 * The Home mount: members only, and any failure of the billing read (or of the
 * notice itself) renders nothing, so Home is never worse off for it (FR-003).
 */
export function HomeOverdueFeeNotice(): JSX.Element | null {
  const session = useSession();
  if (session.state.kind !== 'member') return null;
  return (
    <PortErrorBoundary portName="membershipBilling" fallback={null}>
      <Suspense fallback={null}>
        <OverdueFeeNotice />
      </Suspense>
    </PortErrorBoundary>
  );
}
